import React, { useState, useEffect } from "react";
import {
  getCookiePreferences,
  saveCookiePreferences,
  acceptAllCookies,
  declineNonEssentialCookies,
  hasConsent,
  CookiePreferences,
} from "../utils/cookieManager";

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    if (!hasConsent()) {
      // Load any existing preferences
      const existing = getCookiePreferences();
      if (existing) {
        setPreferences(existing);
      }
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    acceptAllCookies();
    setIsVisible(false);
  };

  const handleDecline = () => {
    declineNonEssentialCookies();
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowCustomize(true);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences);
    setIsVisible(false);
    setShowCustomize(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    // Essential cookies cannot be disabled
    if (key === "essential") return;
    
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Main Cookie Banner */}
      {!showCustomize && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 animate-slide-up">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-white rounded-lg shadow-2xl border-2 border-brand-dark-blue/20 p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                {/* Cookie Icon */}
                <div className="flex-shrink-0 hidden sm:block">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-brand-dark-blue to-brand-cyan rounded-full flex items-center justify-center shadow-lg">
                    <svg
                      className="w-7 h-7 sm:w-8 sm:h-8 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v2h-2zm0 4h2v2h-2zm4 0h2v2h-2zm-4 4h2v2h-2zm4 0h2v2h-2z" />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold text-brand-dark-blue mb-2">
                    Cookie Consent
                  </h3>
                  <p className="text-gray-700 text-sm sm:text-base mb-4">
                    We use cookies to enhance your browsing experience, analyze
                    site traffic, and personalize content. By clicking "Accept
                    All", you consent to our use of cookies. Click "Allow Only Necessary Cookies" to
                    accept only essential cookies, or "Customize" to choose
                    which cookies to allow.
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-gray-600">
                    <a
                      href="/privacy-policy"
                      className="text-brand-cyan hover:text-brand-dark-blue underline font-medium"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-gray-400">•</span>
                    <a
                      href="/terms-of-service"
                      className="text-brand-cyan hover:text-brand-dark-blue underline font-medium"
                    >
                      Terms of Service
                    </a>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button
                    onClick={handleDecline}
                    className="px-6 py-3 text-brand-dark-blue border-2 border-brand-dark-blue/60 hover:border-brand-dark-blue hover:bg-brand-dark-blue/5 font-semibold transition-all rounded-md shadow-sm hover:shadow-md duration-200 text-sm sm:text-base"
                  >
                    Allow Only Necessary Cookies
                  </button>
                  <button
                    onClick={handleCustomize}
                    className="px-6 py-3 text-brand-dark-blue border-2 border-brand-dark-blue/60 hover:border-brand-dark-blue hover:bg-brand-dark-blue/5 font-semibold transition-all rounded-md shadow-sm hover:shadow-md duration-200 text-sm sm:text-base"
                  >
                    Customize
                  </button>
                  <button
                    onClick={handleAccept}
                    className="px-6 py-3 text-white bg-gradient-to-r from-brand-dark-blue to-brand-cyan hover:from-brand-cyan hover:to-brand-dark-blue font-bold transition-all rounded-md shadow-md hover:shadow-lg hover:scale-105 duration-200 text-sm sm:text-base"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customize Panel */}
      {showCustomize && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 animate-slide-up">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-white rounded-lg shadow-2xl border-2 border-brand-dark-blue/20 p-6 sm:p-8 max-h-[80vh] overflow-y-auto">
              <div className="mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-brand-dark-blue mb-2">
                  Cookie Preferences
                </h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  Choose which cookies you want to allow. Essential cookies are
                  required for the website to function and cannot be disabled.
                </p>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-6 mb-6">
                {/* Essential Cookies */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-semibold text-brand-dark-blue">
                        Essential Cookies
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        Required for the website to function properly. These
                        cannot be disabled.
                      </p>
                    </div>
                    <div className="flex items-center flex-shrink-0">
                      <div className="w-14 h-7 bg-brand-dark-blue rounded-full flex items-center justify-center cursor-not-allowed opacity-60">
                        <span className="text-white text-xs font-bold">ON</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-semibold text-brand-dark-blue">
                        Analytics Cookies
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        Help us understand how visitors interact with our website
                        by collecting and reporting information anonymously.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference("analytics")}
                      className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 flex-shrink-0 ${
                        preferences.analytics
                          ? "bg-gradient-to-r from-brand-dark-blue to-brand-cyan"
                          : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                          preferences.analytics ? "translate-x-7" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-semibold text-brand-dark-blue">
                        Marketing Cookies
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        Used to track visitors across websites to display
                        relevant advertisements.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference("marketing")}
                      className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 flex-shrink-0 ${
                        preferences.marketing
                          ? "bg-gradient-to-r from-brand-dark-blue to-brand-cyan"
                          : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                          preferences.marketing ? "translate-x-7" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-semibold text-brand-dark-blue">
                        Functional Cookies
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        Enable enhanced functionality and personalization, such
                        as remembering your preferences.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference("functional")}
                      className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 flex-shrink-0 ${
                        preferences.functional
                          ? "bg-gradient-to-r from-brand-dark-blue to-brand-cyan"
                          : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                          preferences.functional
                            ? "translate-x-7"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowCustomize(false);
                    setPreferences({
                      essential: true,
                      analytics: false,
                      marketing: false,
                      functional: false,
                    });
                  }}
                  className="px-6 py-3 text-brand-dark-blue border-2 border-brand-dark-blue/60 hover:border-brand-dark-blue hover:bg-brand-dark-blue/5 font-semibold transition-all rounded-md shadow-sm hover:shadow-md duration-200 text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-3 text-white bg-gradient-to-r from-brand-dark-blue to-brand-cyan hover:from-brand-cyan hover:to-brand-dark-blue font-bold transition-all rounded-md shadow-md hover:shadow-lg hover:scale-105 duration-200 text-sm sm:text-base"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
